<!--@format-->
<template>
    <div class="updates-widget">
        <div class="widget-contents-container">
            <div class="widget-header">
                <h3>{{ isLoading ? 'Loading...' : currentStep.title }}</h3>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="isLoading">
                    <loading-screen-svg />
                </div>
                <div v-else class="widget-container">
                    <updates-widget-content-step v-if="currentStep === steps.content" ref="contentStep"
                        :imageData="update.content.imageData" @next-step="goToStep('users')"
                        @update:image-uploaded="updateImage" />

                    <updates-widget-users-step v-if="currentStep === steps.users" ref="usersStep"
                        @closeWidget="closeWidget($event)" @next-step="goToStep('details')" />

                    <updates-widget-details-step v-if="currentStep === steps.details" ref="listStep"
                        @close-widget="closeWidget($event)" @go-to-step="goToStep($event)"
                        @next-step="goToStep('schedule')" @unschedule="unscheduleUpdate()"
                        :imageData="update.content.imageData" />

                    <updates-widget-schedule-step v-if="currentStep === steps.schedule" ref="scheduleStep"
                        @next-step="closeWidget()" />
                </div>
            </div>
            <div class="widget-footer">
                <div class="cancel-container">
                    <app-button class="cancel-btn" @click="closeWidget"> Cancel </app-button>
                </div>
                <div v-if="update.status !== 'SCHEDULED'" class="action-container">
                    <app-button class="upload-first-button" @click="currentStep.firstButtonAction">
                        <div v-if="currentStep.arrow">
                            <u6-short-arrow-left-svg />
                        </div>
                        {{ currentStep.firstButton }}
                    </app-button>
                    <app-button class="save-btn" :disabled="loadingData.value" @click="currentStep.secondButtonAction">
                        {{ currentStep.secondButton }}
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const LoadingScreenSvg = () => import('@/components/svg/loading-screen-svg');
const UpdatesWidgetContentStep = () =>
    import('@/components/sliding-widgets/updates-widget/updates-widget-content-step');
const UpdateWidgetUsersStep = () =>
    import('@/components/sliding-widgets/updates-widget/updates-widget-users-step');
const UpdateWidgetDetailsStep = () =>
    import('@/components/sliding-widgets/updates-widget/updates-widget-details-step');
const UpdatesWidgetScheduleStep = () =>
    import('@/components/sliding-widgets/updates-widget/updates-widget-schedule-step');
const U6ShortArrowSvg = () => import('@/components/svg/grid/u6-short-arrow-svg');

import EventBus from '@/scripts/event-bus.js';
import { UpdatesService } from '@/scripts/services/massive-notifications-service';
import { usersEnum } from '@/components/sliding-widgets/updates-widget/update-constants'

export default {
    name: 'updates-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'loading-screen-svg': LoadingScreenSvg,
        'updates-widget-content-step': UpdatesWidgetContentStep,
        'updates-widget-users-step': UpdateWidgetUsersStep,
        'updates-widget-details-step': UpdateWidgetDetailsStep,
        'updates-widget-schedule-step': UpdatesWidgetScheduleStep,
        'u6-short-arrow-left-svg': U6ShortArrowSvg,

    },

    provide() {
        return {
            update: this.update,
            loadingData: this.loadingData,
            userConstant: this.userConstant
        };
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,
            loadingData: { value: false },
            userConstant: usersEnum,
            steps: {
                content: {
                    arrow: false,
                    title: 'Update Content',
                    firstButton: 'Preview',
                    secondButton: 'Save & Continue',
                    loading: false,
                    firstButtonAction: () => {
                        this.openPreviewWidget();
                    },

                    secondButtonAction: () => {
                        this.$refs.contentStep.next();
                    },
                },

                users: {
                    arrow: true,
                    title: 'Users',
                    firstButton: ' Back',
                    secondButton: 'Save & Continue',
                    loading: false,
                    firstButtonAction: () => {
                        this.goToPreviousStep();
                    },

                    secondButtonAction: () => {
                        this.$refs.usersStep.next();
                    },
                },

                details: {
                    arrow: true,
                    title: 'Update Details',
                    firstButton: ' Back',
                    secondButton: 'Save & Continue',
                    loading: false,
                    firstButtonAction: () => {
                        this.goToPreviousStep();
                    },

                    secondButtonAction: () => {
                        this.$refs.listStep.next();
                    },
                },

                schedule: {
                    arrow: true,
                    title: 'Schedule',
                    firstButton: ' Back',
                    secondButton: 'Schedule',
                    loading: false,
                    firstButtonAction: () => {
                        this.goToPreviousStep();
                    },

                    secondButtonAction: () => {
                        this.$refs.scheduleStep.patchUpdate()
                    },
                },
            },

            currentStep: null,
            update: {
                id: this.widget.updateId,
                clientId: this.widget.clientId,
                status: null,
                sendMode: null,
                sendDate: null,
                timezone: this.$helpers.guessTimezone().name,
                strategies: [
                    {
                        name: 'Yes',
                        value: true,
                    },
                    {
                        name: 'No',
                        value: false,
                    },
                ],

                content: {
                    body: '',
                    title: '',
                    imageUrlToSend: null,
                    imageData: { url: null, name: null },
                    callToAction: { name: 'Yes', value: true },
                    buttonText: '',
                    buttonUrlLink: '',
                },

                users: [{ name: usersEnum.allUsers, value: false }, { name: usersEnum.masterAgency, value: false }, { name: usersEnum.vendor, value: false }, { name: usersEnum.partner, value: false }],
            },

        };
    },

    watch: {
        'update.users': {
            deep: true,
            handler() {
                if (this.update.users.some(element => element.name === this.userConstant.allUsers && element.value)) {
                    this.update.users.forEach(item => {
                        item.value = true
                    })
                }
            }
        }
    },

    created() {
        EventBus.$on('loading-widget', () => {
            if (this.loadingData.value) {
                this.loadingData.value = false
            } else {
                this.loadingData.value = true

            }
        });

        if (this.widget.updateId) {
            this.goToStep('details');
            this.isLoading = true
            this.getUpdateById(this.update.id).then(() => {
                if (!this.isUsersValid()) {
                    this.goToStep('content');
                }
            }).catch().finally(() => {
                this.isLoading = false
            })
        } else { this.goToStep('content') };
    },

    beforeDestroy() {
        EventBus.$off('loading-widget');
    },

    methods: {
        isUsersValid() {
            const isValid = this.update.users.some((item) => item.value === true)
            return isValid
        },

        goToStep(step) {
            this.currentStep = this.steps[step];
        },

        updateImage(event) {
            this.update.content.imageData = event
        },

        goToPreviousStep() {
            if (this.currentStep === this.steps.users) {
                this.goToStep('content');
            } else if (this.currentStep === this.steps.details) {
                this.goToStep('users');
            } else if (this.currentStep === this.steps.schedule) {
                this.goToStep('details');
            }
        },

        closeWidget() {
            EventBus.$emit('update-board');
            EventBus.$emit('close-widget', this.widget);
        },

        unscheduleUpdate() {
            this.isLoading = true;
            this.getUpdateById(this.update.id).then(() => {
                EventBus.$emit('update-board');
                this.isLoading = false;
            });
        },

        openPreviewWidget() {
            EventBus.$emit('open-widget', {
                name: 'preview-update',
                body: this.update.content.body,
                title: this.update.content.title,
                image: this.update.content.imageData,
                buttonText: this.update.content.buttonText,
                buttonUrl: this.update.content.buttonUrlLink,
            });
        },

        getUpdateById(id) {
            return new Promise((resolve, reject) => {
                UpdatesService.getById(id)
                    .then((response) => {
                        const { AudienceAgency, AudienceAll, AudienceMasterAgency, AudiencePartner, Body, CallToActionLink, CallToActionText, Image, IsCallToActionEnabled, SendMode, Status, Title } = response.data.data
                        this.update.status = Status
                        this.update.sendMode = SendMode
                        this.update.content.body = Body
                        this.update.content.title = Title
                        this.update.content.imageData.url = Image || null
                        this.update.content.imageUrlToSend = Image || null
                        this.update.content.callToAction.name = IsCallToActionEnabled ? 'Yes' : 'No'
                        this.update.content.callToAction.value = Boolean(IsCallToActionEnabled)
                        this.update.content.buttonText = CallToActionText
                        this.update.content.buttonUrlLink = CallToActionLink
                        this.update.users = [{ name: usersEnum.allUsers, value: AudienceAll, allSelection: true }, { name: usersEnum.masterAgency, value: AudienceMasterAgency, allSelection: false }, { name: usersEnum.vendor, value: AudienceAgency, allSelection: false }, { name: usersEnum.partner, value: AudiencePartner, allSelection: false }]
                    }).catch(reject).finally(resolve);
            });
        },
    },
};
</script>

<style>
.updates-widget {
    color: #382153;
}

.updates-widget .title-section {
    font-weight: 600;
    display: flex;
    align-items: center;
}

.updates-widget .action-container {
    display: flex;
    gap: 15px;
}

.updates-widget .action-container .save-btn {
    width: 139px;
    min-height: 40px;
}

.updates-widget .upload-first-button {
    border: 2px solid #18aeff;
    background-color: transparent;
    color: #18aeff;
    transition: none;
    min-width: 128px;
    min-height: 40px;
}

.updates-widget .upload-first-button:hover {
    border: 2px solid #18aeff;
    background-color: #18aeff;
    color: #ffffff;
}

.updates-widget .upload-first-button:hover svg path {
    fill: #ffffff;
}

.updates-widget .u6-short-arrow-svg .a {
    fill: #18aeff;
    width: 8px;
    height: 12px;
}

.updates-widget .u6-short-arrow-svg {
    width: 8px;
    height: 12px;
    margin-right: 13px;
    display: flex;
    justify-content: center;
}

.updates-widget .action-container .back-btn:hover {
    background: #e1e0e5;
}

.updates-widget .cancel-btn {
    background: transparent;
    border: 2px solid #CAC6CB;
    border-radius: 20px;
    font: normal normal medium 14px/25px Roboto;
    letter-spacing: 0px;
    color: #382153;
    min-width: 128px;
    min-height: 40px;
}

.updates-widget .cancel-btn:hover {
    background: #e9e9ef;
}

.updates-widget .error {
    color: #F30070;
}

.updates-widget .app-input label {
    display: none;
}

.updates-widget .error-message,
.updates-widget .app-input label,
.updates-widget .error-msg {
    display: none;
}

.updates-widget .widget-contents-container .content-create {
    padding: 0;
}

.updates-widget .content-create .app-text-editor-wrapper,
.updates-widget .content-create .app-text-editor,
.updates-widget .content-create .widget-container {
    height: 100%;
}

.updates-widget .content-create .widget-container .controls {
    display: none;
}

.updates-widget .content-wrapper {
    padding: 15px 35px !important;
}
</style>
